import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { SmartLink } from "@features/smart-link";

import { type DocumentSourceType, mapDocumentSource } from "@entities/document";

import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Checkbox } from "@shared/ui/checkbox";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Select } from "@shared/ui/select";
import { Textarea } from "@shared/ui/textarea/textarea";
import { Body, Heading } from "@shared/ui/typography";
import { mod } from "@shared/utils";

import {
  InstructorsFormSchema,
  type InstructorsFormValues,
} from "@/_pages/instructors/model";

import s from "./popup-form.module.scss";

type InstructorsFormProps = {
  isOpen?: boolean;
  onSuccess?: () => void;
  bgColor?: "milk" | "white" | "transparent";
  title?: string;
  description?: string;
  className?: string;
  document?: DocumentSourceType | null;
};

const maxLengthTextArea = 500;

type SendStatus = "idle" | "sending" | "success" | "error";

const PopupForm = (props: InstructorsFormProps) => {
  const {
    onSuccess,
    isOpen,
    bgColor = "white",
    title,
    description,
    document,
    className,
  } = props;
  const mods = mod(s, {
    bgColor,
  });
  const [status, setStatus] = useState<SendStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm<InstructorsFormValues>({
    resolver: zodResolver(InstructorsFormSchema),
    defaultValues: {
      fullName: "",
      time: "1 час",
      type: "Индивидуально",
      comment: "",
      consent: false,
    },
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
      setStatus("idle");
      setErrorMessage(null);
    }
  }, [isOpen]);

  const onSubmit = async (data: InstructorsFormValues) => {
    setStatus("sending");
    setErrorMessage(null);

    try {
      const res = await fetch("/api/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const body = await res.json().catch(() => null);

      if (!res.ok) {
        setStatus("error");
        setErrorMessage(
          body?.message || body?.error || "Не удалось отправить сообщение",
        );
        return;
      }
      setStatus("success");

      setTimeout(() => {
        reset();
        setStatus("idle");
        onSuccess?.();
      }, 2000);
    } catch (e) {
      setStatus("error");
      setErrorMessage("Ошибка сети или сервера");
    }
  };

  const buttonText = useMemo(() => {
    switch (status) {
      case "sending":
        return "Отправляем...";
      case "success":
        return "Отправлено!";
      default:
        return "Отправить";
    }
  }, [status]);

  const commentValue = watch("comment") ?? "";
  const commentLength = commentValue.length;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={clsx(s.root, className, mods)}
    >
      <div className={s.header}>
        <Heading level={5}>{title}</Heading>
        <Body size="caption-small" className={s.description}>
          {description}
        </Body>
      </div>
      <div className={s.formBody}>
        <div className={s.field}>
          <Label
            htmlFor="instructors-full-name"
            className={s.fieldLabel}
            requiredMark
          >
            Имя Фамилия
          </Label>
          <Input
            bgColor={bgColor}
            id="instructors-full-name"
            placeholder="Петя Иванов"
            {...register("fullName")}
            error={errors.fullName?.message}
          />
        </div>

        <div className={clsx(s.field, s.topField)}>
          <Label
            htmlFor="instructors-phone"
            className={s.fieldLabel}
            requiredMark
          >
            Количество часов
          </Label>
          <Controller
            name="time"
            control={control}
            render={({ field }) => (
              <Select {...field} options={["1 час", "2 часа", "3 часа"]} />
            )}
          />
        </div>

        <div className={s.field}>
          <Label
            htmlFor="instructors-email"
            className={s.fieldLabel}
            requiredMark
          >
            Тип занятия
          </Label>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={["Индивидуально", "2 человека", "5-8 человек"]}
              />
            )}
          />
        </div>

        <div className={s.field}>
          <Label htmlFor="instructors-comment" className={s.fieldLabel}>
            Комментарий
          </Label>
          <Textarea
            id="instructors-comment"
            bgColor={bgColor}
            placeholder="Добавить свой вопрос"
            {...register("comment")}
            error={errors.comment?.message}
            commentLength={commentLength}
            maxLengthUI={maxLengthTextArea}
          />
        </div>
      </div>
      <div className={s.footer}>
        <Checkbox error={errors.consent?.message} {...register("consent")}>
          Я подтверждаю, что ознакомлен(а) с{" "}
          <SmartLink source={mapDocumentSource(document)} openInNewTab>
            Политикой конфиденциальности
          </SmartLink>{" "}
          и даю согласие на обработку персональных данных
        </Checkbox>
        <div className={s.relativeBox}>
          <ButtonWithIcon
            variant="secondary-dark"
            type="submit"
            iconName="plus"
            iconStrokeColor="white"
            iconFillColor="cadmium"
            disabled={status === "sending" || status === "success"}
          >
            {buttonText}
          </ButtonWithIcon>
          {status === "error" && (
            <Body
              size="caption"
              weight="semiBold"
              className={s.statusMessageError}
            >
              {errorMessage || "Ошибка отправки"}
            </Body>
          )}

          {status === "success" && (
            <Body
              size="caption"
              weight="semiBold"
              className={s.statusMessageSuccess}
            >
              Сообщение успешно отправлено!
            </Body>
          )}
        </div>
      </div>
    </form>
  );
};

PopupForm.displayName = "PopupForm";

export { PopupForm };
